import { Mail } from 'lucide-react'
import { useState } from 'react'
import { Outlet } from 'react-router'
import { Header } from '@/components/layout/header'
import { MobileNav } from '@/components/layout/mobile-nav'
import { Sidebar } from '@/components/layout/sidebar'
import { OnboardingFlow } from '@/components/onboarding/onboarding-flow'
import { SuspensionBanner } from '@/components/suspension-banner'
import { useAuth } from '@/lib/auth-context'

export function AppLayout(): React.JSX.Element {
  const { user, account } = useAuth()
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false)
  const [mobileNavOpen, setMobileNavOpen] = useState(false)

  // Use ref to avoid unmounting OnboardingFlow during transient null account states
  // (e.g., when TanStack Query refetches cause brief null flicker)
  const showOnboarding = account === null ? false : !account.onboarding_completed

  // Dim sidebar only during email verification step — once verified,
  // sidebar is fully interactive so users can explore while onboarding.
  const emailNotVerified = showOnboarding && user?.emailVerified === false

  function handleToggleSidebar(): void {
    if (window.innerWidth < 1024) {
      setMobileNavOpen((prev) => !prev)
    } else {
      setSidebarCollapsed((prev) => !prev)
    }
  }

  return (
    <div className="flex h-screen bg-surface-1">
      {/* Desktop sidebar — dimmed only during email verification step */}
      <div className="hidden lg:block">
        <div
          style={{
            opacity: emailNotVerified ? 0.45 : 1,
            pointerEvents: emailNotVerified ? 'none' : 'auto',
            transition: 'opacity 0.3s ease',
          }}
        >
          <Sidebar collapsed={sidebarCollapsed} />
        </div>
      </div>

      {/* Mobile nav overlay */}
      <MobileNav open={mobileNavOpen} onClose={() => setMobileNavOpen(false)} />

      {/* Main content area */}
      <div className="flex flex-1 flex-col overflow-hidden">
        <Header onToggleSidebar={handleToggleSidebar} />

        {emailNotVerified && (
          <div className="flex items-center gap-2 border-b border-amber-200 bg-amber-50 px-4 py-2.5 text-sm text-amber-800">
            <Mail className="h-4 w-4 shrink-0" />
            <span>Verify your email to unlock all features</span>
          </div>
        )}

        {showOnboarding ? (
          <OnboardingFlow />
        ) : (
          <>
            <SuspensionBanner />
            <main className="flex-1 overflow-y-auto p-4 lg:p-6">
              <Outlet />
            </main>
          </>
        )}
      </div>
    </div>
  )
}
